// frontend/src/utils/promotionStatus.js
//
// What a promotion's status actually IS right now, as opposed to the status
// column the server last wrote. A promotion the merchant left 'active' is over
// once its end date has passed, and the grid and timeline show that without
// waiting for a job to rewrite the row.
//
// The comparison is a business-date question ("is this promotion over, where
// the store is?"), so it takes the store's calendar (useStoreToday), not the
// UTC day and not the viewer's. `start_date` / `end_date` are plain DateFields
// (backend/api/models.py `Promotion`): calendar dates, with no zone of their
// own to convert through.

/**
 * @param {{status?: string, end_date?: string}} promo — a promotion row.
 * @param {string} today — the store's today as 'YYYY-MM-DD' (useStoreToday),
 *   or '' when the store's zone isn't known yet. On '' the end-date override is
 *   skipped and the stored status stands: without an anchor there is no honest
 *   answer to "is it over", and calling a live promotion finished is worse than
 *   waiting a tick for the zone. Never defaulted to a clock.
 * @returns {string} 'draft' | 'scheduled' | 'active' | 'completed' | ''
 */
export function promotionEffectiveStatus(promo, today) {
  if (!promo) return '';
  if (promo.status === 'draft') return 'draft';
  if (promo.status === 'scheduled') return 'scheduled';
  // 'YYYY-MM-DD' strings compare lexicographically in calendar order, so this
  // needs no parsing and no Date — which is the point: a Date would reintroduce
  // a zone conversion on a value that is a calendar date, not an instant.
  if (promo.status === 'active' && promo.end_date && today && promo.end_date < today) {
    return 'completed';
  }
  // `promo.status`, never defaulted: Promotion.status is
  // `CharField(choices=..., default='draft')` with no null=True, so the
  // serializer always sends one. Substituting 'active' would paint an unknown
  // promotion as running; an absent status renders as a neutral chip.
  return promo.status;
}
